import React from 'react';
import { FaEnvelope, FaPhone, FaMapMarkerAlt, FaLinkedin, FaGlobe, FaBriefcase, FaGraduationCap, FaTools, FaProjectDiagram } from 'react-icons/fa';

const ResumePreview = ({ resumeData, template }) => {
  const {
    personalInfo,
    workExperience,
    education,
    skills,
    projects
  } = resumeData;

  return (
    <div className="bg-white p-8 shadow-inner min-h-[1056px] w-full max-w-[816px] mx-auto" style={{ aspectRatio: '1/1.4142' }}>
      {/* Header */}
      <div className="border-b-2 border-blue-500/20 pb-6 mb-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-4 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
          {personalInfo.fullName || 'Your Name'}
        </h1>
        <div className="flex flex-wrap gap-4 text-sm text-gray-600">
          {personalInfo.email && (
            <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 hover:bg-blue-100 transition-colors duration-200">
              <FaEnvelope className="text-blue-500" />
              <span>{personalInfo.email}</span>
            </div>
          )}
          {personalInfo.phone && (
            <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-purple-50 hover:bg-purple-100 transition-colors duration-200">
              <FaPhone className="text-purple-500" />
              <span>{personalInfo.phone}</span>
            </div>
          )}
          {personalInfo.location && (
            <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-green-50 hover:bg-green-100 transition-colors duration-200">
              <FaMapMarkerAlt className="text-green-500" />
              <span>{personalInfo.location}</span>
            </div>
          )}
          {personalInfo.linkedIn && (
            <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 hover:bg-blue-100 transition-colors duration-200">
              <FaLinkedin className="text-blue-600" />
              <a href={personalInfo.linkedIn} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                LinkedIn
              </a>
            </div>
          )}
          {personalInfo.portfolio && (
            <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-50 hover:bg-indigo-100 transition-colors duration-200">
              <FaGlobe className="text-indigo-500" />
              <a href={personalInfo.portfolio} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">
                Portfolio
              </a>
            </div>
          )}
        </div>
      </div>

      {/* Work Experience */}
      {workExperience.length > 0 && (
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <FaBriefcase className="text-xl text-blue-500" />
            <h2 className="text-2xl font-bold text-gray-900">Work Experience</h2>
          </div>
          <div className="space-y-6">
            {workExperience.map((exp, index) => (
              <div key={index} className="pl-4 border-l-2 border-blue-500/20 hover:border-blue-500 transition-colors duration-300">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-semibold text-gray-900 text-lg">{exp.position}</h3>
                    <p className="text-blue-600 font-medium">{exp.company}</p>
                  </div>
                  <div className="text-sm bg-blue-50 px-3 py-1 rounded-full text-blue-600 font-medium">
                    {exp.startDate} - {exp.endDate}
                  </div>
                </div>
                <p className="mt-2 text-gray-700 text-sm whitespace-pre-line leading-relaxed">{exp.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Education */}
      {education.length > 0 && (
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <FaGraduationCap className="text-xl text-purple-500" />
            <h2 className="text-2xl font-bold text-gray-900">Education</h2>
          </div>
          <div className="space-y-6">
            {education.map((edu, index) => (
              <div key={index} className="pl-4 border-l-2 border-purple-500/20 hover:border-purple-500 transition-colors duration-300">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-semibold text-gray-900 text-lg">{edu.degree}</h3>
                    <p className="text-purple-600 font-medium">{edu.school}</p>
                  </div>
                  <div className="text-sm bg-purple-50 px-3 py-1 rounded-full text-purple-600 font-medium">
                    {edu.startDate} - {edu.endDate}
                  </div>
                </div>
                {edu.description && (
                  <p className="mt-2 text-gray-700 text-sm whitespace-pre-line leading-relaxed">{edu.description}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Skills */}
      {skills.length > 0 && (
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <FaTools className="text-xl text-green-500" />
            <h2 className="text-2xl font-bold text-gray-900">Skills</h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {skills.map((skill, index) => (
              <span
                key={index}
                className="px-3 py-1 bg-green-50 text-green-700 rounded-full text-sm font-medium
                         hover:bg-green-100 transition-colors duration-200"
              >
                {skill}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Projects */}
      {projects.length > 0 && (
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <FaProjectDiagram className="text-xl text-indigo-500" />
            <h2 className="text-2xl font-bold text-gray-900">Projects</h2>
          </div>
          <div className="space-y-6">
            {projects.map((project, index) => (
              <div key={index} className="pl-4 border-l-2 border-indigo-500/20 hover:border-indigo-500 transition-colors duration-300">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-semibold text-gray-900 text-lg">{project.name}</h3>
                    <p className="text-indigo-600 font-medium">{project.technologies}</p>
                  </div>
                </div>
                <p className="mt-2 text-gray-700 text-sm whitespace-pre-line leading-relaxed">{project.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ResumePreview;
